import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  CheckType,
  CommittedAs,
  QARequestButtonState,
  WebinarRegistrationDto,
} from "integration/api/Deal/models";
import { InitialState } from "./models";
import { Tabs } from "application/constants/deal";
import {
  getDeal,
  getDealCommitment,
  getDealReferralLink,
  registerWebinarQuestion,
  setInvestment,
} from "./actions";

const initialState: InitialState = {
  tab: Tabs.Overview,
  deal: null,
  isLoading: false,
  commitment: {
    amount: 0,
    commitment: 0,
    overcommitment: 0,
    investmentDoneAs: CommittedAs.INDIVIDUAL,
    legalEntityId: null,
    commitmentId: null,
    checkType: CheckType.NO_CHECKS,
    bonusBalance: 0,
    managementFee: 0,
    managementFeeWithBonus: 0,
    managementFeePercentage: 0,
    canUpdate: false,
  },
  shortReferralLink: "",
};

const initialWebinarRegistration: WebinarRegistrationDto = {
  answer: null,
  question: null,
  answerDate: null,
  noQuestion: false,
  replyLanguage: null,
};

const dealSlice = createSlice({
  name: "dealSlice",
  initialState,
  reducers: {
    setTab: (state, action: PayloadAction<Tabs>) => {
      state.tab = action.payload;
    },
  },
  extraReducers(builder) {
    // get deal
    builder.addCase(getDeal.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getDeal.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(getDeal.fulfilled, (state, action) => {
      state.isLoading = false;
      state.deal = action.payload;
    });
    //get deal commitment
    builder.addCase(getDealCommitment.fulfilled, (state, action) => {
      state.commitment.amount = action.payload.amount;
      state.commitment.commitment = action.payload.commitment;
      state.commitment.overcommitment = action.payload.overcommitment;
      state.commitment.commitmentId = action.payload.commitmentId;
      state.commitment.checkType = action.payload.checkType;
      state.commitment.bonusBalance = action.payload.bonusBalance;
      state.commitment.managementFee = action.payload.managementFee;
      state.commitment.managementFeeWithBonus =
        action.payload.managementFeeWithBonus;
      state.commitment.managementFeePercentage =
        action.payload.managementFeePercentage;
      state.commitment.canUpdate = action.payload.canUpdate;
    });
    //set investment
    builder.addCase(setInvestment.fulfilled, (state, action) => {
      state.commitment.amount = action.payload.amount;
      state.commitment.commitment = action.payload.commitment;
      state.commitment.overcommitment = action.payload.overcommitment;
    });

    // registerWebinarQuestion
    builder.addCase(registerWebinarQuestion.fulfilled, (state, { payload }) => {
      if (!state.deal) {
        return;
      }

      state.deal.requestQaButtonState = payload.noQuestion
        ? QARequestButtonState.REGISTRATION_CONFIRMED
        : QARequestButtonState.VIEW_QUESTION;

      if (!payload.noQuestion) {
        state.deal.webinarRegistration = {
          ...initialWebinarRegistration,
          question: payload.question ?? "",
        };
      }
    });
    //get deal referralLink
    builder.addCase(getDealReferralLink.fulfilled, (state, action) => {
      state.shortReferralLink = action.payload.shortReferralLink;
    });
  },
});

export const { setTab } = dealSlice.actions;

export default dealSlice.reducer;
